import { useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./QuillEditor.module.scss";

const TOOLBAR_LABELS: Record<string, string | Record<string, string>> = {
  "ql-bold": "Bold",
  "ql-italic": "Italic",
  "ql-code-block": "Code block",
  "ql-clean": "Clear formatting",
  "ql-indent": { "-1": "Decrease indent", "+1": "Increase indent" },
  "ql-list": { ordered: "Ordered list", bullet: "Bullet list" },
  "ql-align": {
    "": "Align left",
    center: "Align center",
    right: "Align right",
    justify: "Justify",
  },
  "ql-header": { "1": "Heading 1", "2": "Heading 2", "3": "Heading 3", "": "Normal text" },
  "ql-color": {
    "": "Default color",
    black: "Black",
    grey: "Grey",
    red: "Red",
    orange: "Orange",
    green: "Green",
    blue: "Blue",
    violet: "Violet",
  },
};

const PICKER_LABELS: Record<string, string> = {
  "ql-header": "Heading level",
  "ql-color": "Text color",
  "ql-align": "Text alignment",
};

const defaultToolbar = [
  [
    { header: [1, 2, 3, false] },
    "bold",
    "italic",
    { color: ["", "black", "grey", "red", "orange", "green", "blue", "violet"] },
    { list: "ordered" },
    { list: "bullet" },
    { indent: "-1" },
    { indent: "+1" },
    { align: [] },
    "code-block",
    "clean",
  ],
];

// A Quill toolbar config is an array of button groups (rendered with a gap
// between groups); each group is an array of controls — either a named button
// ("bold") or a picker/config object ({ header: [1, 2, 3, false] })
type QuillToolbarEntry = string | Record<string, unknown>;

interface QuillEditorProps {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  toolbarOptions?: QuillToolbarEntry[][];
  ariaLabel?: string;
}

function patchQuillA11y(container: HTMLElement, editorRoot: HTMLElement, ariaLabel: string) {
  editorRoot.setAttribute("aria-label", ariaLabel);
  editorRoot.setAttribute("role", "textbox");
  editorRoot.setAttribute("aria-multiline", "true");

  const toolbar = container?.querySelector(".ql-toolbar");
  if (!toolbar) return;

  toolbar.setAttribute("role", "toolbar");
  toolbar.setAttribute("aria-label", "Text formatting options");

  toolbar.querySelectorAll("button").forEach(button => {
    if (button.getAttribute("aria-label")) return;

    for (const [classname, label] of Object.entries(TOOLBAR_LABELS)) {
      if (!button.classList.contains(classname)) continue;

      const value = button.getAttribute("value");
      const resolved = typeof label === "string" ? label : label[value ?? ""];
      if (resolved) button.setAttribute("aria-label", resolved);
      break;
    }
  });

  toolbar.querySelectorAll(".ql-picker").forEach(picker => {
    for (const [classname, label] of Object.entries(PICKER_LABELS)) {
      if (!picker.classList.contains(classname)) continue;
      picker.querySelector(".ql-picker-label")?.setAttribute("aria-label", label);

      const optionLabels = TOOLBAR_LABELS[classname];
      if (optionLabels && typeof optionLabels !== "string") {
        picker.querySelectorAll(".ql-picker-item").forEach(item => {
          const btnValue = item.getAttribute("data-value") ?? "";
          const resolved = optionLabels[btnValue];
          if (resolved) item.setAttribute("aria-label", resolved);
        });
      }
      break;
    }
  });

  toolbar.querySelectorAll("select").forEach(select => {
    const pickerClass = Array.from(select.classList).find(c => c.startsWith("ql-"));
    if (pickerClass && PICKER_LABELS[pickerClass]) {
      select.setAttribute("aria-label", PICKER_LABELS[pickerClass]);
    }
  });

  const tooltip = container?.querySelector(".ql-tooltip");
  if (tooltip) {
    const preview = tooltip.querySelector("a.ql-preview");
    if (preview) preview.setAttribute("aria-label", "Link preview");

    const input = tooltip.querySelector("input[type='text']");
    if (input) input.setAttribute("aria-label", "URL input");
  }
}

/**
 * Rich text editor wrapping React Quill
 *
 * React Quill does not provide any built-in accessibility support.
 * Its toolbar renders icon-only buttons and picker dropdowns without ARIA attributes,
 * which triggers accessibility audit errors. There is no config or prop to customize this,
 * so we have to add ARIA attributes manually after the editor mounts.
 *
 * It appears this version of React Quill may have been a deliberate choice, but it's
 * worth considering a migration to a more modern and accessible text editor in the future.
 *
 */
export function QuillEditor({
  value = "",
  onChange,
  placeholder = "Write your note here...",
  toolbarOptions = defaultToolbar,
  ariaLabel = "Rich text editor. Press Escape to exit.",
}: QuillEditorProps) {
  const quillRef = useRef<ReactQuill>(null);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const editorRoot = editor.root;
    const container = editorRoot.closest(".quill");
    if (!container) return;

    patchQuillA11y(container as HTMLElement, editorRoot, ariaLabel);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        editorRoot.blur();
      }
    };

    editorRoot.addEventListener("keydown", handleKeyDown);

    return () => {
      editorRoot.removeEventListener("keydown", handleKeyDown);
    };
  }, [ariaLabel, toolbarOptions]);

  return (
    <ReactQuill
      ref={quillRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      modules={{ toolbar: toolbarOptions }}
    />
  );
}
